import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@parsina/framework';
import { Crosshair, Loader2, Map as MapIcon, MapPin, X } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { MAP_LIB_BASE, MAP_TILE_URL } from '../../lib/runtimeEnv';
import { GEO_ERRORS, currentPosition, reverseGeocode } from '../../lib/geocode';

/**
 * «یافتن نشانی از روی موقعیت» — one picker, two ways in.
 *
 * Used by the clinic profile («کلینیک من» → مشخصات) and by the clinic
 * registration wizard. ONE component on purpose: those two screens ask for the
 * same thing and used to answer it differently — the wizard had a map and the
 * profile had nothing — which is the whole reason this exists.
 *
 * ── Why two ways in, and why the map is the optional one ─────────────────────
 *
 * «موقعیت فعلی من» needs no map, no tiles and no CDN: it is one browser API and
 * one geocoder call. The map needs Leaflet, tile images and a reachable CDN,
 * from a network inside Iran where any of the three can be missing. So the map
 * is loaded ONLY when someone opens it, its failure is reported rather than
 * shown as a grey box, and the button that does not need it keeps working when
 * it fails. Both hosts are overridable per environment — see `runtimeEnv.js`.
 *
 * That ordering is also why one component serves the desktop workbench and the
 * mobile reduction unchanged. On a phone the map is the SECONDARY path: it opens
 * closed, and «موقعیت فعلی من» is both one tap and far more accurate there than
 * a fingertip on a 260px map — which is the reduction «کلینیک من» needs, not a
 * second component to keep in step.
 *
 * ── What it hands back ───────────────────────────────────────────────────────
 *
 * `onPick({latitude, longitude, province, city, address})` — the coordinates
 * always, the three names only when the geocoder answered. The caller decides
 * what to overwrite; the geocoder failing is NOT a failure of the pick, because
 * a point on the map is worth storing even when nobody can name it.
 */

/** Tehran, for a map that has nowhere better to open — unless the caller says where. */
const FALLBACK_VIEW = [35.6892, 51.389, 11];
const LOAD_TIMEOUT_MS = 15_000;

/**
 * Leaflet, loaded once per document and shared by every instance.
 *
 * A promise rather than a boolean: two pickers on one page (the wizard's and a
 * future one) must not race two `<script>` tags, and a failure has to be
 * remembered so the second open reports it immediately instead of waiting out
 * the timeout again.
 */
let leafletPromise = null;

function loadLeaflet() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no-window'));
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    const done = (fn) => (arg) => { clearTimeout(timer); fn(arg); };
    const timer = setTimeout(() => {
      leafletPromise = null;            // let a later open try again
      reject(new Error('timeout'));
    }, LOAD_TIMEOUT_MS);

    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = `${MAP_LIB_BASE}/leaflet.css`;
      document.head.appendChild(link);
    }

    const existing = document.getElementById('leaflet-js');
    if (existing) {
      existing.addEventListener('load', done(() => resolve(window.L)));
      existing.addEventListener('error', done(() => { leafletPromise = null; reject(new Error('network')); }));
      return;
    }
    const script = document.createElement('script');
    script.id = 'leaflet-js';
    script.src = `${MAP_LIB_BASE}/leaflet.js`;
    script.onload = done(() => (window.L ? resolve(window.L) : reject(new Error('network'))));
    script.onerror = done(() => { leafletPromise = null; reject(new Error('network')); });
    document.head.appendChild(script);
  });
  return leafletPromise;
}

/** The Leaflet surface. Mounted only while the map is open. */
function MapSurface({ latitude, longitude, openAt, onPoint, onFailed }) {
  const { t } = useTranslation();
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const pointRef = useRef(onPoint);
  const [state, setState] = useState('loading');   // loading | ready | failed

  // The click handler is registered once with Leaflet but must always call the
  // CURRENT callback — re-registering it on every render would leak listeners.
  pointRef.current = onPoint;

  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !hostRef.current || mapRef.current) return;
        const [fallbackLat, fallbackLng, fallbackZoom] = openAt || FALLBACK_VIEW;
        const center = latitude && longitude ? [latitude, longitude] : [fallbackLat, fallbackLng];
        const map = L.map(hostRef.current).setView(center, latitude ? 15 : fallbackZoom);
        L.tileLayer(MAP_TILE_URL, { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(map);
        if (latitude && longitude) markerRef.current = L.marker(center).addTo(map);
        map.on('click', (e) => {
          const { lat, lng } = e.latlng;
          if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
          else markerRef.current = L.marker([lat, lng]).addTo(map);
          pointRef.current?.({ latitude: lat, longitude: lng });
        });
        mapRef.current = map;
        setState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setState('failed');
        onFailed?.();
      });

    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow coordinates set from outside the map — «موقعیت فعلی من» while the
  // map is open must move the pin, not leave it on the old point.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !latitude || !longitude || typeof window === 'undefined' || !window.L) return;
    const at = [latitude, longitude];
    if (markerRef.current) markerRef.current.setLatLng(at);
    else markerRef.current = window.L.marker(at).addTo(map);
    map.setView(at, Math.max(map.getZoom(), 15));
  }, [latitude, longitude]);

  if (state === 'failed') {
    return (
      <div
        className="flex min-h-[120px] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border bg-muted p-4 text-center"
        data-testid="location-map-failed"
      >
        <p className="m-0 text-[13px] font-semibold text-foreground">{t('clinic.location.mapFailed')}</p>
        <p className="m-0 text-xs text-muted-foreground">{t('clinic.location.mapFailedHint')}</p>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-lg border border-border" data-testid="location-map">
      <div ref={hostRef} className="h-[260px] w-full" />
      {state === 'loading' ? (
        <div className="absolute inset-0 flex items-center justify-center bg-card/75 text-[13px] font-semibold text-primary">
          {t('clinic.location.loadingMap')}
        </div>
      ) : null}
    </div>
  );
}

/**
 * `abroad` — the clinic is outside Iran: names come back in the panel's language
 * and are not fitted to Iran's province list. `openAt` — `[lat, lng, zoom]` for a
 * map with no pin yet, in place of Tehran.
 */
export default function LocationPicker({ latitude, longitude, onPick, disabled, abroad = false, openAt }) {
  const { t, locale } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);   // { tone: 'error'|'info', text }

  const has = latitude != null && longitude != null;

  /** Coordinates in, names out — the one path both entry points share. */
  const resolve = useCallback(async (point) => {
    setBusy(true);
    setMessage(null);
    try {
      const named = await reverseGeocode({ ...point, abroad, language: locale });
      onPick?.({ ...point, ...named });
      // An answer with nothing in it is not an error — the point is still good.
      if (!named.province && !named.city && !named.address) {
        setMessage({ tone: 'info', text: t('clinic.location.noAddress') });
      }
    } catch {
      onPick?.(point);
      setMessage({ tone: 'info', text: t('clinic.location.geocodeFailed') });
    } finally {
      setBusy(false);
    }
  }, [onPick, t, abroad, locale]);

  const useMyLocation = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      const point = await currentPosition();
      await resolve(point);
    } catch (err) {
      const reason = err?.message;
      const key = {
        [GEO_ERRORS.denied]: 'clinic.location.geoDenied',
        [GEO_ERRORS.unavailable]: 'clinic.location.geoUnavailable',
        [GEO_ERRORS.timeout]: 'clinic.location.geoTimeout',
        [GEO_ERRORS.unsupported]: 'clinic.location.geoUnsupported',
      }[reason] || 'clinic.location.geoUnavailable';
      setMessage({ tone: 'error', text: t(key) });
      setBusy(false);
    }
  }, [resolve, t]);

  return (
    <div className="flex flex-col gap-2" data-testid="location-picker">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={useMyLocation}
          disabled={disabled || busy}
          data-testid="location-use-mine"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <Crosshair className="h-4 w-4" aria-hidden="true" />}
          {t('clinic.location.useMine')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          data-testid="location-toggle-map"
        >
          <MapIcon className="h-4 w-4" aria-hidden="true" />
          {open ? t('clinic.location.hideMap') : t('clinic.location.pickOnMap')}
        </Button>
        {has ? (
          <span
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
            data-testid="location-coords"
          >
            <MapPin className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
              {Number(latitude).toFixed(5)}, {Number(longitude).toFixed(5)}
            </span>
            {!disabled ? (
              <button
                type="button"
                onClick={() => { onPick?.({ latitude: null, longitude: null }); setMessage(null); }}
                className="inline-flex items-center rounded p-0.5 text-muted-foreground hover:text-destructive"
                aria-label={t('clinic.location.clear')}
                data-testid="location-clear"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ) : null}
          </span>
        ) : null}
      </div>

      {message ? (
        <p
          className={`m-0 text-xs ${message.tone === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}
          role="status"
          data-testid="location-message"
        >
          {message.text}
        </p>
      ) : (
        <p className="m-0 text-xs text-muted-foreground">{t('clinic.location.hint')}</p>
      )}

      {open ? (
        <MapSurface
          latitude={latitude}
          longitude={longitude}
          openAt={openAt}
          onPoint={resolve}
          onFailed={() => setMessage({ tone: 'info', text: t('clinic.location.mapFailed') })}
        />
      ) : null}

      {busy ? (
        <p className="m-0 text-xs font-semibold text-primary" data-testid="location-busy">
          {t('clinic.location.resolving')}
        </p>
      ) : null}
    </div>
  );
}
